import activityApprovedUrl from "assets/icons/ActivityApproved.svg"
import activityRejectedUrl from "assets/icons/ActivityRejected.svg"
import calendarAddedUrl from "assets/icons/CalendarAdded.svg"
import calendarCancelledUrl from "assets/icons/CalendarCancelled.svg"
import calendarLocationUrl from "assets/icons/CalendarLocation.svg"
import calendarOtherUrl from "assets/icons/CalendarOther.svg"
import calendarTimeUrl from "assets/icons/CalendarTime.svg"
import deadlineUrl from "assets/icons/Deadline.svg"
import overdueUrl from "assets/icons/Overdue.svg"
import { forwardRef, type HTMLAttributes } from "react"
import { useTranslation, type TFunction } from "react-i18next"
import { modifiers } from "ui-tools"
import s from "./NotificationItem.module.scss"

export type NotificationType =
  | "activity_approved"
  | "activity_rejected"
  | "attendee_added"
  | "attendee_removed"
  | "date_time_changed"
  | "deadline_approaching_one_day"
  | "deadline_approaching_one_week"
  | "instance_cancelled"
  | "location_changed"
  | "overdue_one_day"
  | "overdue_one_week"
  | "recertification_period_started"
  | "self_registration"
  | "waiting_list_added"
  | "waiting_list_promoted"

export type NotificationData = {
  id: string
  notification_type: NotificationType
  read_at: string | null
  data: {
    title: string
    message: string
    path?: string | null
  }
  created_at: string
  is_read: boolean
}

export type NotificationItemProps = {
  notification: NotificationData
  onRead?: (notificationId: string) => void
  onActivate?: (notification: NotificationData) => void
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case "attendee_added":
    case "self_registration":
    case "waiting_list_added":
    case "waiting_list_promoted":
      return calendarAddedUrl
    case "attendee_removed":
    case "instance_cancelled":
      return calendarCancelledUrl
    case "location_changed":
      return calendarLocationUrl
    case "date_time_changed":
      return calendarTimeUrl
    case "activity_approved":
      return activityApprovedUrl
    case "activity_rejected":
      return activityRejectedUrl
    // Recertification shares the deadline clock — that's what Figma specifies,
    // it isn't a missing asset.
    case "deadline_approaching_one_day":
    case "deadline_approaching_one_week":
    case "recertification_period_started":
      return deadlineUrl
    case "overdue_one_day":
    case "overdue_one_week":
      return overdueUrl
    // Fallback for types the backend adds before we ship an icon.
    default:
      return calendarOtherUrl
  }
}

export const NotificationItemSkeleton = () => (
  <li className={s.notificationItem__skeleton}>
    <div className={s.notificationItem__skeletonIcon} />
    <div className={s.notificationItem__skeletonContent}>
      <div className={s.notificationItem__skeletonTitle} />
      <div className={s.notificationItem__skeletonMessage} />
    </div>
  </li>
)

// Extends li attributes so a Headless `MenuItem as={Fragment}` wrapper can
// forward its ref + props (onClick, role, etc.) onto the row — that's what
// closes the menu when a notification with a deep link is clicked.
export const NotificationItem = forwardRef<
  HTMLLIElement,
  NotificationItemProps & HTMLAttributes<HTMLLIElement>
>(({ notification, onRead, onActivate, ...rest }, ref) => {
  const { t } = useTranslation()

  const handleClick = () => {
    if (!notification.is_read && onRead) {
      onRead(notification.id)
    }
    if (onActivate) {
      onActivate(notification)
    }
  }

  const notificationIconUrl = getNotificationIcon(
    notification.notification_type
  )

  return (
    <li
      ref={ref}
      className={modifiers(
        s,
        "notificationItem",
        notification.is_read ? "read" : "unread"
      )}
      {...rest}
    >
      <button className={s.notificationItem__button} onClick={handleClick}>
        <div
          className={s.notificationItem__indicator}
          style={{ visibility: notification.is_read ? "hidden" : "visible" }}
          aria-hidden="true"
        />

        <div className={s.notificationItem__iconWrapper}>
          <img
            className={s.notificationItem__icon}
            src={notificationIconUrl}
            alt=""
            aria-hidden="true"
          />
        </div>

        <div className={s.notificationItem__content}>
          <div className={s.notificationItem__header}>
            <div className={s.notificationItem__title}>
              {notification.data.title}
            </div>
            <div className={s.notificationItem__timestamp}>
              {formatTimestamp(notification.created_at, t)}
            </div>
          </div>
          <div className={s.notificationItem__message}>
            {notification.data.message}
          </div>
        </div>
      </button>
    </li>
  )
})

NotificationItem.displayName = "NotificationItem"

const formatTimestamp = (timestamp: string, t: TFunction): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) {
    return t("notifications.timestamp.JUST_NOW")
  } else if (diffInMinutes < 60) {
    return t("notifications.timestamp.MINUTES_AGO", { count: diffInMinutes })
  } else if (diffInHours < 24) {
    return t("notifications.timestamp.HOURS_AGO", { count: diffInHours })
  } else if (diffInDays < 7) {
    return t("notifications.timestamp.DAYS_AGO", { count: diffInDays })
  } else {
    return date.toLocaleDateString()
  }
}
